import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../utils/axios";

/**
 * Fetch Salary Head Master list
 */
export const fetchSalaryHeadMaster = createAsyncThunk(
    'payroll/fetchSalaryHeadMaster',
    async (limit: number = 200): Promise<any[]> => {
        const res = await axios.get(`/api/v1/payroll/salaryHead?limit=${limit}`);
        return res.data?.data || [];
    }
);

/**
 * Fetch Employee Salary Config (latest by effectiveFrom DESC)
 */
export const fetchEmployeeSalaryConfig = createAsyncThunk(
    'payroll/fetchEmployeeSalaryConfig',
    async ({ employeeId, limit = 10, sortLatest = false }: { employeeId: string; limit?: number; sortLatest?: boolean }): Promise<any[]> => {
        const sortParam = sortLatest ? `&sort=-effectiveFrom&_t=${Date.now()}` : '';
        const res = await axios.get(`/api/v1/payroll/employeeSalaryConfig?employee=${employeeId}&limit=${limit}${sortParam}`);
        return res.data?.data || [];
    }
);

/**
 * Fetch Salary Grade by ID
 */
export const fetchSalaryGradeById = createAsyncThunk(
    'payroll/fetchSalaryGradeById',
    async (gradeId: string): Promise<any> => {
        const res = await axios.get(`/api/v1/payroll/salaryGrade/${gradeId}`);
        return res.data?.data || res.data;
    }
);

/**
 * Fetch Employee Timesheet Monthly
 */
export const fetchEmployeeTimesheetMonthly = createAsyncThunk(
    'payroll/fetchEmployeeTimesheetMonthly',
    async ({ employeeId, year, month }: { employeeId: string; year: number; month: number }): Promise<any> => {
        const res = await axios.get(`/api/v1/employee/timesheet/monthly/${employeeId}`, {
            params: { year, month },
            headers: { 'x-silent': '1' }
        });
        return res.data;
    }
);

/**
 * Fetch Employee Leave Requests (approved/pending/rejected) — same source as the
 * mobile app calendar. Returns raw leave requests each with dates[], status, leaveConfig.
 */
export const fetchEmployeeLeaveRequests = createAsyncThunk(
    'payroll/fetchEmployeeLeaveRequests',
    async ({ employeeId }: { employeeId: string }): Promise<any[]> => {
        const res = await axios.get(`/api/v1/leaves/request/employee/${employeeId}`, {
            headers: { 'x-silent': '1' }
        });
        return res.data?.data || [];
    }
);

/**
 * Fetch Employee Daily Attendance (fallback)
 */
export const fetchEmployeeDailyAttendance = createAsyncThunk(
    'payroll/fetchEmployeeDailyAttendance',
    async ({ accountId, startDate, endDate, employeeId }: { accountId: string; startDate: string; endDate: string; employeeId: string }): Promise<any> => {
        const res = await axios.get(`/api/v1/employee/dailyAttendance`, {
            params: { accountId, startDate, endDate, employee: employeeId },
            headers: { 'x-silent': '1' }
        });
        return res.data;
    }
);

/**
 * Fetch Holidays in date range
 */
export const fetchHolidaysInRange = createAsyncThunk(
    'payroll/fetchHolidaysInRange',
    async ({ startDate, endDate }: { startDate: string; endDate: string }): Promise<any> => {
        const res = await axios.get(`/api/v1/holidays`, {
            params: { dateFrom: startDate, dateTo: endDate, isActive: true },
            headers: { 'x-silent': '1' }
        });
        return res.data;
    }
);

/**
 * Fetch Weekly Timesheets (paginated list when year/month omitted)
 */
export const fetchEmployeeWeeklyTimesheets = createAsyncThunk(
    'payroll/fetchEmployeeWeeklyTimesheets',
    async ({ employeeId, page = 1, limit = 50 }: { employeeId: string; page?: number; limit?: number }): Promise<any> => {
        const res = await axios.get(`/api/v1/employee/timesheet/weekly/${employeeId}`, {
            params: { page, limit },
            headers: { 'x-silent': '1' }
        });
        return res.data;
    }
);

/**
 * Fetch a single Weekly Timesheet with full day-wise breakdown
 */
export const fetchEmployeeWeeklyTimesheetDetail = createAsyncThunk(
    'payroll/fetchEmployeeWeeklyTimesheetDetail',
    async ({ employeeId, year, month, week }: { employeeId: string; year: number; month: number; week?: number }): Promise<any> => {
        const res = await axios.get(`/api/v1/employee/timesheet/weekly/${employeeId}`, {
            params: week !== undefined ? { year, month, week } : { year, month },
            headers: { 'x-silent': '1' }
        });
        return res.data;
    }
);

/**
 * Approve / Reject / Reopen a Weekly Timesheet
 * action: 'approve' | 'reject' | 'pending'
 */
export const approveEmployeeWeeklyTimesheet = createAsyncThunk(
    'payroll/approveEmployeeWeeklyTimesheet',
    async (
        { approverId, employeeId, timesheetId, action, rejectedComments, userRole }:
        { approverId: string; employeeId: string; timesheetId: string; action: 'approve' | 'reject' | 'pending'; rejectedComments?: string; userRole?: string }
    ): Promise<any> => {
        const res = await axios.put(`/api/v1/employee/timesheet/approve/${approverId}`, {
            employeeId,
            timesheetId,
            action,
            rejectedComments,
            userRole,
        });
        return res.data;
    }
);

// ===================== ATTENDANCE CORRECTION (Process Salary) =====================

// Backend errors come as {message} or {error}; the caller toasts this text itself.
const attendanceError = (e: any): Error => {
    const d = e?.response?.data;
    return new Error((typeof d === 'string' ? d : d?.message || d?.error) || e?.message || 'Request failed');
};
const SILENT = { headers: { 'x-silent': '1' } };

/**
 * Mark (or overwrite) one day's attendance. The backend upserts by employee + date.
 */
export const markEmployeeAttendance = createAsyncThunk(
    'payroll/markEmployeeAttendance',
    async (
        { employeeId, accountId, date, attendanceType, createdBy }:
        { employeeId: string; accountId: string; date: string; attendanceType: 'Present' | 'Absent' | 'Half-Day'; createdBy?: string }
    ): Promise<any> => {
        try {
            const res = await axios.post(`/api/v1/employee/dailyAttendance`, {
                employee: employeeId,
                accountId,
                dates: [date],
                attendanceType,
                workMode: 'Office',
                source: 'Manual',
                // Present/Half-Day hours are derived from the employee's shift when omitted.
                ...(attendanceType === 'Absent' ? { totalWorkedHours: 0 } : {}),
                createdBy,
            }, SILENT);
            return res.data;
        } catch (e) { throw attendanceError(e); }
    }
);

/**
 * Clear one day's attendance mark.
 */
export const clearEmployeeAttendance = createAsyncThunk(
    'payroll/clearEmployeeAttendance',
    async ({ attendanceId }: { attendanceId: string }): Promise<any> => {
        try {
            const res = await axios.delete(`/api/v1/employee/dailyAttendance/${attendanceId}`, SILENT);
            return res.data;
        } catch (e) { throw attendanceError(e); }
    }
);

/**
 * Mark leave for an employee; created already approved.
 */
export const markEmployeeLeaveOnBehalf = createAsyncThunk(
    'payroll/markEmployeeLeaveOnBehalf',
    async (
        { employeeId, approvedBy, leaveConfig, dates, comments }:
        { employeeId: string; approvedBy: string; leaveConfig: string; dates: string[]; comments?: string }
    ): Promise<any> => {
        try {
            const res = await axios.post(`/api/v1/leaves/request/${employeeId}/on-behalf/${approvedBy}`, { leaveConfig, dates, comments }, SILENT);
            return res.data;
        } catch (e) { throw attendanceError(e); }
    }
);

/**
 * Cancel a leave request. Cancels the WHOLE request, so a multi-day leave loses all its dates.
 */
export const cancelEmployeeLeaveRequest = createAsyncThunk(
    'payroll/cancelEmployeeLeaveRequest',
    async ({ requestId, employeeId }: { requestId: string; employeeId: string }): Promise<any> => {
        try {
            const res = await axios.put(`/api/v1/leaves/request/${requestId}/cancel`, { employeeId }, SILENT);
            return res.data;
        } catch (e) { throw attendanceError(e); }
    }
);

/**
 * The processed salary for an employee + month (YYYY-MM), or null. Kept out of the salary slice so the list behind the form isn't replaced.
 */
export const fetchProcessedSalaryForMonth = createAsyncThunk(
    'payroll/fetchProcessedSalaryForMonth',
    async ({ accountId, employeeId, month }: { accountId: string; employeeId: string; month: string }): Promise<any | null> => {
        const res = await axios.get(`/api/v1/salary`, {
            params: { accountId, employee: employeeId, month, limit: 1 },
            headers: { 'x-silent': '1' }
        });
        const rows = res.data?.data;
        return Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
    }
);
